import React, { useState, useMemo } from 'react';
import { Card, List, Tag, Typography, Row, Col, Statistic, Input, Table, Space } from 'antd';
import { CalendarOutlined, ShoppingOutlined, SearchOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { arrivalData } from './data';
import type { ArrivalEntry, ProductSummary } from '../../types';
import styles from './index.module.css';

const { Title, Text } = Typography;
const { Search } = Input;

const Arrival: React.FC = () => {
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [productSummaryVisible, setProductSummaryVisible] = useState<boolean>(false);

    // 按时间降序排列数据
    const sortedData = useMemo<ArrivalEntry[]>(() => {
        return [...arrivalData].sort((a, b) => {
            const dateA = new Date(a.time);
            const dateB = new Date(b.time);
            return dateB.getTime() - dateA.getTime(); // 降序排列
        });
    }, []);

    // 根据搜索关键词过滤数据
    const filteredData = useMemo<ArrivalEntry[]>(() => {
        if (!searchKeyword.trim()) {
            return sortedData;
        }

        const keyword = searchKeyword.trim().toLowerCase();
        return sortedData
            .map(dayData => {
                const filteredGoods = dayData.goods.filter(good =>
                    good.name.toLowerCase().includes(keyword)
                );
                if (filteredGoods.length > 0) {
                    return {
                        ...dayData,
                        goods: filteredGoods
                    };
                }
                return null;
            })
            .filter((item): item is ArrivalEntry => item !== null);
    }, [sortedData, searchKeyword]);

    // 计算总览统计（基于过滤后的数据）
    const overviewStats = useMemo(() => {
        const totalDays = filteredData.length;
        const totalGoods = filteredData.reduce((sum, day) => sum + day.goods.length, 0);
        const totalQuantity = filteredData.reduce((sum, day) => {
            return sum + day.goods.reduce((daySum, good) => daySum + good.count, 0);
        }, 0);

        return {
            totalDays,
            totalGoods,
            totalQuantity
        };
    }, [filteredData]);

    // 按商品名称汇总（不区分时间）
    const productSummary = useMemo<ProductSummary[]>(() => {
        const productMap = new Map<string, ProductSummary>();
        
        // 遍历所有数据，按商品名称汇总
        sortedData.forEach(dayData => {
            dayData.goods.forEach(good => {
                const existing = productMap.get(good.name);
                if (existing) {
                    existing.totalCount += good.count;
                    existing.arrivalTimes += 1;
                } else {
                    productMap.set(good.name, {
                        productName: good.name,
                        totalCount: good.count,
                        arrivalTimes: 1
                    });
                }
            });
        });

        return Array.from(productMap.values()).sort((a, b) => b.totalCount - a.totalCount);
    }, [sortedData]);

    // 根据搜索关键词过滤商品汇总
    const filteredProductSummary = useMemo<ProductSummary[]>(() => {
        if (!searchKeyword.trim()) {
            return productSummary;
        }

        const keyword = searchKeyword.trim().toLowerCase();
        return productSummary.filter(product =>
            product.productName.toLowerCase().includes(keyword)
        );
    }, [productSummary, searchKeyword]);

    // 高亮显示匹配的文本
    const highlightText = (text: string, keyword: string): React.ReactNode => {
        if (!keyword.trim()) {
            return text;
        }

        const parts = text.split(new RegExp(`(${keyword})`, 'gi'));
        return parts.map((part, index) =>
            part.toLowerCase() === keyword.toLowerCase() ? (
                <span key={index} style={{ backgroundColor: '#fff566', fontWeight: 'bold' }}>
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    // 格式化日期显示
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
        const weekday = weekdays[date.getDay()];
        return `${year}年${month}月${day}日 星期${weekday}`;
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Title level={2} className={styles.title}>到货商品记录</Title>
            </div>

            {/* 搜索框 */}
            <Card className={styles.searchCard}>
                <Search
                    placeholder="搜索商品名称..."
                    allowClear
                    enterButton={<SearchOutlined />}
                    size="large"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onSearch={(value) => setSearchKeyword(value)}
                    className={styles.searchInput}
                />
            </Card>

            {/* 总览统计 */}
            <Row gutter={[16, 16]} className={styles.overviewRow}>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title="到货天数"
                            value={overviewStats.totalDays}
                            prefix={<CalendarOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title="商品种类数"
                            value={overviewStats.totalGoods}
                            prefix={<ShoppingOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title="商品总数量"
                            value={overviewStats.totalQuantity}
                            prefix={<ShoppingOutlined />}
                            valueStyle={{ color: '#eb2f96' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 商品汇总表格 */}
            <Card 
                title="商品汇总统计" 
                className={styles.summaryCard}
                extra={
                    <span 
                        onClick={() => setProductSummaryVisible(!productSummaryVisible)}
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                    >
                        {productSummaryVisible ? (
                            <Space>
                                <UpOutlined />
                                <Text>收起</Text>
                            </Space>
                        ) : (
                            <Space>
                                <DownOutlined />
                                <Text>展开</Text>
                            </Space>
                        )}
                    </span>
                }
            >
                {productSummaryVisible && (
                    filteredProductSummary.length > 0 ? (
                        <Table
                            columns={[
                                {
                                    title: '商品名称',
                                    dataIndex: 'productName',
                                    key: 'productName',
                                    width: '40%',
                                    render: (text) => highlightText(text, searchKeyword),
                                },
                                {
                                    title: '总数量',
                                    dataIndex: 'totalCount',
                                    key: 'totalCount',
                                    width: '30%',
                                    align: 'center',
                                    sorter: (a, b) => a.totalCount - b.totalCount,
                                    defaultSortOrder: 'descend',
                                    render: (count) => (
                                        <span style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '16px' }}>
                                            {count}
                                        </span>
                                    ),
                                },
                                {
                                    title: '到货次数',
                                    dataIndex: 'arrivalTimes',
                                    key: 'arrivalTimes',
                                    width: '30%',
                                    align: 'center',
                                    sorter: (a, b) => a.arrivalTimes - b.arrivalTimes,
                                    render: (times) => (
                                        <Tag color="purple" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                            {times} 次
                                        </Tag>
                                    ),
                                },
                            ]}
                            dataSource={filteredProductSummary}
                            rowKey="productName"
                            pagination={{
                                pageSize: 20,
                                showSizeChanger: true,
                                showTotal: (total) => `共 ${total} 种商品`,
                            }}
                        />
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Text type="secondary" style={{ fontSize: '16px' }}>
                                未找到匹配的商品
                            </Text>
                        </div>
                    )
                )}
            </Card>

            {/* 到货记录列表 */}
            <div className={styles.arrivalList}>
                {filteredData.length > 0 ? (
                    filteredData.map((dayData, index) => (
                    <Card
                        key={dayData.time}
                        className={styles.dayCard}
                        title={
                            <div className={styles.cardTitle}>
                                <CalendarOutlined className={styles.titleIcon} />
                                <Text strong style={{ fontSize: '18px' }}>
                                    {formatDate(dayData.time)}
                                </Text>
                            </div>
                        }
                    >
                        <List
                            dataSource={dayData.goods}
                            renderItem={(item, itemIndex) => (
                                <List.Item className={styles.goodItem}>
                                    <div className={styles.goodContent}>
                                        <Text style={{ fontSize: '16px' }}>
                                            {highlightText(item.name, searchKeyword)}
                                        </Text>
                                        <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                            数量: {item.count}
                                        </Tag>
                                    </div>
                                </List.Item>
                            )}
                        />
                    </Card>
                    ))
                ) : (
                    <Card className={styles.emptyCard}>
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Text type="secondary" style={{ fontSize: '16px' }}>
                                未找到匹配的商品
                            </Text>
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default Arrival;

